import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ProductsService } from '../products.service';
import { Product } from '../models/product';
import { WorkItem } from '../models/work-item';
import { ProductsViewPage } from '../products-view/products-view.page';
import { ProductsCreatePage } from '../products-create/products-create.page';
import { ModalController} from '@ionic/angular';

import { ToastController } from '@ionic/angular';

import { WorkItemAddComponent } from '../work-item-add/work-item-add.component';
import { AuthService } from '../services/auth.service';
import { ApiService } from '../services/api.service';

import { ProductSearchPipe } from '../pipes/productSearchFilter.pipe';

@Component({
  //standalone: true,
  selector: 'app-work-item-chooser',
  templateUrl: './work-item-chooser.component.html',
  styleUrls: ['./work-item-chooser.component.scss']
})
export class WorkItemChooserComponent implements OnInit {

  productsData: Product[];
  filterTerm: string;
  productsChosen: Product[];
  searchText: string;
  accountCurrency: string;

  // Segments
  segment: 'products' | 'ai' | 'recent' = 'products';

  // AI
  aiPrompt: string = '';
  isGenerating = false;
  aiResults: (WorkItem & { chosen?: boolean })[] = [];
  aiChosen: WorkItem[] = [];

  // Recent
  recentWorkItems: (WorkItem & { chosen?: boolean })[] = [];
  recentChosen: WorkItem[] = [];

  currentBusiness: any;

  constructor(
    public productsService: ProductsService, 
    public modalCtrl: ModalController, 
    public toastController: ToastController,
    private authService: AuthService,
    private apiService: ApiService) { }

  ngOnInit() {
    this.productsChosen = [];
    this.aiChosen = [];
    this.recentChosen = [];
    // Get selected Account currency
    this.authService.getSelectedAccount().then((account) => {
      this.accountCurrency = account.currency;
    });

    // Preload business context for AI
    this.apiService.getBusinessList().subscribe((response) => {
      if (response && response.length > 0) {
        this.currentBusiness = response[0];
      }
    });
  }

  ionViewWillEnter() {
    // Used ionViewWillEnter as ngOnInit is not 
    // called due to view persistence in Ionic
    this.getAllProducts();
  }

  getAllProducts() {
    // Get API list of contacts
    this.productsService.getList().subscribe(response => {
      this.productsData = response;
      console.log(response);
    })
  }

  loadRecentWorkItems() {
    this.apiService.getRecentWorkItems(20).subscribe((items) => {
      // Clear chosen flags
      this.recentWorkItems = (items || []).map(i => ({ ...i, chosen: false } as any));
    });
  }

  add() {
    // Check that we have some products
    if (this.segment === 'products' && this.productsChosen.length > 0) {
      const mapped = this.productsChosen.map(p => this.mapProductToWorkItem(p));
      this.modalCtrl.dismiss(mapped);
    }
    if (this.segment === 'ai' && this.aiChosen.length > 0) {
      const mapped = this.aiChosen.map(w => this.sanitizeWorkItemForAdd(w));
      this.modalCtrl.dismiss(mapped);
    }
    if (this.segment === 'recent' && this.recentChosen.length > 0) {
      const mapped = this.recentChosen.map(w => this.sanitizeWorkItemForAdd(w));
      this.modalCtrl.dismiss(mapped);
    }
  }

  mapProductToWorkItem(source: Product): WorkItem {
    const destination: WorkItem = {
      name: source.name,
      description: source.description,
      rate: source.rate,
      quantity: source.quantity,
      taxRate: source.taxRate,
      hasTax: source.hasTax
    } as WorkItem;
    return destination;
  }

  sanitizeWorkItemForAdd(source: Partial<WorkItem>): WorkItem {
    const destination: WorkItem = {
      name: source.name,
      description: source.description,
      rate: source.rate,
      quantity: source.quantity,
      taxRate: source.taxRate,
      hasTax: !!source.hasTax
    } as WorkItem;
    return destination;
  }

  async selectProduct(product) {
    var index = this.productsChosen.findIndex(x => x.id === product.id);
    if (index != -1) {
      this.productsChosen.splice(index, 1);
      product.chosen = false;
    }
    else {
      this.productsChosen.push(product);
      product.chosen = true;
    }
  }

  async selectAI(item: any) {
    const idx = this.aiChosen.indexOf(item);
    if (idx !== -1) {
      this.aiChosen.splice(idx, 1);
      (item as any).chosen = false;
    } else {
      this.aiChosen.push(item);
      (item as any).chosen = true;
    }
  }

  async selectRecent(item: any) {
    const idx = this.recentChosen.findIndex(x => (x as any).id === (item as any).id);
    if (idx !== -1) {
      this.recentChosen.splice(idx, 1);
      (item as any).chosen = false;
    } else {
      this.recentChosen.push(item);
      (item as any).chosen = true;
    }
  }

  async onSegmentChanged(ev: any) {
    this.segment = ev.detail.value;
    if (this.segment === 'recent' && this.recentWorkItems.length === 0) {
      this.loadRecentWorkItems();
    }
  }

  async generateWorkItems() {
    if (!this.currentBusiness) {
      this.presentToast('Please create a business first.');
      return;
    }
    this.isGenerating = true;
    this.aiChosen = [];
    this.apiService.workitemGeneration(this.currentBusiness.id, this.aiPrompt || '').subscribe({
      next: (items) => {
        this.aiResults = (items || []).map(i => ({ ...i, chosen: false } as any));
        this.isGenerating = false;
      },
      error: () => {
        this.isGenerating = false;
        this.presentToast('Failed to generate items.');
      }
    });
  }

  getSelectedCount(): number {
    if (this.segment === 'products') return this.productsChosen.length;
    if (this.segment === 'ai') return this.aiChosen.length;
    if (this.segment === 'recent') return this.recentChosen.length;
    return 0;
  }

  async addWorkItemButton() {  
    const modal = await this.modalCtrl.create({  
      component: WorkItemAddComponent,
      componentProps: {
        currencyCode: this.accountCurrency
      }
    });

    modal.onDidDismiss()
      .then((data) => {

        const workitem = data.data;

        if (workitem != null){
          // Toast notification
          this.presentToast('A new Work Item was created.');

          // Refresh the data
          //this.getAllProducts();

          // Close the Chooser Component with the newly created Work Item
          this.modalCtrl.dismiss([workitem]);

        }
        else {
          // User has dismissed creating a new contact without saving
        }
    });

    await modal.present();
  }

  async presentToast(message) {
    const toast = await this.toastController.create({
      message: message,
      duration: 2000
    });
    toast.present();
  }

  dismiss() {  
    this.modalCtrl.dismiss();
  }

}
